<script>
import Avatar from 'next/avatar/Avatar.vue';
import { vOnClickOutside } from '@vueuse/components';

export default {
  components: {
    Avatar,
  },
  directives: {
    onClickOutside: vOnClickOutside,
  },
  props: {
    assistants: {
      type: Array,
      default: () => [],
    },
    selectedId: {
      type: [Number, null],
      default: null,
    },
  },
  emits: ['select'],
  data() {
    return {
      isOpen: false,
      searchQuery: '',
    };
  },
  computed: {
    selectedAssistant() {
      return this.assistants.find(a => a.id === this.selectedId);
    },
    filteredAssistants() {
      if (!this.searchQuery) return this.assistants;
      const query = this.searchQuery.toLowerCase();
      return this.assistants.filter(
        a =>
          a.name.toLowerCase().includes(query) ||
          a.email.toLowerCase().includes(query)
      );
    },
  },
  methods: {
    toggleDropdown() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.$nextTick(() => {
          this.$refs.searchInput?.focus();
        });
      }
    },
    selectAssistant(assistant) {
      this.$emit('select', assistant);
      this.isOpen = false;
      this.searchQuery = '';
    },
    closeDropdown() {
      this.isOpen = false;
      this.searchQuery = '';
    },
  },
};
</script>

<template>
  <div v-on-click-outside="closeDropdown" class="relative">
    <button
      type="button"
      class="w-full flex items-center justify-between px-3 py-2 border border-n-weak rounded-lg bg-n-alpha-1 text-n-slate-12 hover:bg-n-alpha-2 focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent"
      @click="toggleDropdown"
    >
      <div v-if="selectedAssistant" class="flex items-center gap-2">
        <Avatar
          :src="selectedAssistant.avatar_url"
          :name="selectedAssistant.name"
          :size="24"
          rounded-full
        />
        <div class="flex flex-col items-start">
          <span class="text-sm font-medium">{{ selectedAssistant.name }}</span>
          <span class="text-xs text-n-slate-11">{{
            selectedAssistant.email
          }}</span>
        </div>
      </div>
      <span v-else class="text-n-slate-11">
        {{ $t('KAPTION_ACCESS_TOKENS.FORM.ASSISTANT.PLACEHOLDER') }}
      </span>
      <fluent-icon
        icon="chevron-down"
        size="16"
        class="text-n-slate-11 transition-transform"
        :class="{ 'rotate-180': isOpen }"
      />
    </button>

    <div
      v-if="isOpen"
      class="absolute z-10 mt-1 w-full bg-n-solid-1 border border-n-weak rounded-lg shadow-lg max-h-64 overflow-hidden"
    >
      <div class="p-2 border-b border-n-weak">
        <input
          ref="searchInput"
          v-model="searchQuery"
          type="text"
          class="w-full px-2 py-1 text-sm border border-n-weak rounded bg-n-alpha-1 text-n-slate-12 placeholder:text-n-slate-11 focus:outline-none focus:ring-1 focus:ring-woot-500"
          :placeholder="$t('KAPTION_ACCESS_TOKENS.FORM.ASSISTANT.SEARCH')"
        />
      </div>
      <div class="overflow-y-auto max-h-48">
        <button
          v-for="assistant in filteredAssistants"
          :key="assistant.id"
          type="button"
          class="w-full flex items-center gap-2 px-3 py-2 hover:bg-n-alpha-2 text-left"
          :class="{
            'bg-woot-50 dark:bg-woot-800': assistant.id === selectedId,
          }"
          @click="selectAssistant(assistant)"
        >
          <Avatar
            :src="assistant.avatar_url"
            :name="assistant.name"
            :size="32"
            rounded-full
          />
          <div class="flex flex-col">
            <span class="text-sm font-medium text-n-slate-12">{{
              assistant.name
            }}</span>
            <span class="text-xs text-n-slate-11">{{ assistant.email }}</span>
          </div>
        </button>
        <div
          v-if="filteredAssistants.length === 0"
          class="px-3 py-4 text-center text-sm text-n-slate-11"
        >
          {{ $t('KAPTION_ACCESS_TOKENS.FORM.ASSISTANT.NO_RESULTS') }}
        </div>
      </div>
    </div>
  </div>
</template>
